import React, { FC, useContext, useCallback } from 'react';
import { FocusOn } from 'react-focus-on';
import { BrandingLogo } from './BrandingLogo';
import { NavElement } from './NavElement';
import { MenuToggle } from './MenuToggle';
import { MenuList } from './MenuList';
import { NavMenu, NavMenuVariant } from './SideNavTypes';
import { NavContextProvider } from './NavContextProvider';
import { NavContext } from './NavContext';
import { size } from 'lodash';
import { DropdownType, Dropdown } from '@trip-ux/components';
import {
  MIL_SECOND_FACTOR,
  ORG_CATEGORY_AGENCY,
  AUTH_HEADERS,
  REPORT_PERMISSIONS,
  AGENT_ADMINISTRATION_PERMISSIONS,
  ORG_CATEGORY_BROKER,
  TOOLS_PERMISSIONS,
  ADVISOR_ADMINISTRATION_PERMISSIONS,
  ADVISOR_REPORT_PERMISSIONS
} from '@trip-ux/constants';
import { showComponent } from '@trip-ux/common';
import { checkFeatureFlag, ReduxStore } from '@trip-ux/redux-store';
import { FEATURE_FLAGS } from '@trip-ux/constants';

const calculateSideNavMenuList = (claims, menuList, userType, featureFlags = []) => {
  let finalMenuList = claims.includes(REPORT_PERMISSIONS.REPORT_READ)
    ? menuList
    : menuList.filter(menuItem => menuItem.navId !== 'myReports'); //exclude reports

  // ✅ Permission check for myAdvisorReports
  finalMenuList = claims.includes(ADVISOR_REPORT_PERMISSIONS.ADVISOR_REPORT)
    ? finalMenuList
    : finalMenuList.filter(menuItem => menuItem.navId !== 'myAdvisorReports');

  //excludes agency admin - users for SSO and excludes agency admin - users-internal for CIAM
  finalMenuList =
    AUTH_HEADERS.LAN_ID === userType
      ? finalMenuList.filter(menuItem => menuItem.navId !== 'users')
      : finalMenuList.filter(menuItem => menuItem.navId !== 'users-internal');
  finalMenuList = claims.includes(AGENT_ADMINISTRATION_PERMISSIONS.AGENT_ADMINISTRATION_READ)
    ? finalMenuList
    : finalMenuList.filter(
      menuItem =>
        menuItem.navId !== 'users-internal' && menuItem.navId !== 'users'
    );
  finalMenuList = claims.includes(ADVISOR_ADMINISTRATION_PERMISSIONS.PROFILE_ADVISOR_READ)
    ? finalMenuList
    : finalMenuList.filter(
      menuItem =>
        menuItem.navId !== 'advisorAdmin'
    );
  finalMenuList = claims.includes(TOOLS_PERMISSIONS.RATES_READ)
    ? finalMenuList
    : finalMenuList.filter(menuItem => menuItem.navId !== 'MyRates');


  // ✅ Feature flag check for both advisorAdmin and myAdvisorReports
  if (!checkFeatureFlag(featureFlags, FEATURE_FLAGS.ADVISOR)) {
    finalMenuList = finalMenuList.filter(menuItem =>
      menuItem.navId !== 'advisorAdmin' && menuItem.navId !== 'myAdvisorReports'
    );
  }
  return finalMenuList;
};

export const Navigation: FC<NavMenu> = ({
  logo,
  branding,
  mobileMenuItem,
  toggleClose,
  toggleOpen,
  menuList,
  isAuthenticated = false,
  staticSubMenuNavId,
  languageToggleLabel,
  languageToggleAriaLabel,
  languageToggleLink,
  profile,
  contactBackButtonLabel,
  onLanguageToggle,
  contactInfo,
  contactUs,
  userName,
  userType,
  showOrgSearch = false,
  organizationDropDownList = [],
  searchOrganizations = (str: string) => [],
  organizationSelectionChange,
  organizationCode = '',
  organizationText = '',
  organizationCategory = '',
  claims = [],
  featureFlags = []
}) => {
  const organizationSearch = useCallback(str => {
    size(str) >= 1 && searchOrganizations(str);
  }, []);
  const context = useContext(NavContext);
  const shouldShowContact = !context.showContact && showOrgSearch;
  const finalMenuList = calculateSideNavMenuList(claims, menuList, userType, featureFlags);
  return context.isMobile ? (
    <div className={`side-nav-mobile ${context.mobileToggle ? 'show-mobile-drop' : ''}`}>
      <div className={`d-flex flex-row ${context.mobileToggle ? 'inverted' : ''}`} role="menubar">
        <BrandingLogo {...branding} />
        {showComponent(
          mobileMenuItem,
          <NavElement {...mobileMenuItem} className="mobile-item order-2 ml-auto" variant={NavMenuVariant.VERTICAL} />
        )}
        <MenuToggle ariaControls="nav-mobile-control" toggleClose={toggleClose} toggleOpen={toggleOpen} />
      </div>
      {showComponent(
        context.mobileToggle,
        <FocusOn
          enabled={context.mobileToggle}
          autoFocus={true}
          returnFocus={true}
          onClickOutside={e => {
            context.setMobileToggle(false);
            context.setClickedOutside(true);
            setTimeout(function () {
              context.setClickedOutside(false);
            }, MIL_SECOND_FACTOR);
          }}
          onEscapeKey={() => context.setMobileToggle(false)}>
          {userType !== AUTH_HEADERS.CIAM_ID &&
            showComponent(
              shouldShowContact,
              <div className="organization-selection-mob">
                <Dropdown
                  variant={DropdownType.AUTO_SUGGEST}
                  name="organizationCode"
                  placeholder={organizationText}
                  onChange={organizationSelectionChange}
                  onAutoSuggestChange={organizationSearch}
                  options={organizationDropDownList}
                  value={organizationCode}
                  id="organizationCode"
                />
              </div>
            )}
          <MenuList
            userName={userName}
            isAuthenticated={isAuthenticated}
            items={finalMenuList}
            languageToggleLabel={languageToggleLabel}
            languageToggleAriaLabel={languageToggleAriaLabel}
            languageToggleLink={languageToggleLink}
            onLanguageToggle={onLanguageToggle}
            profile={profile}
            id="nav-mobile-control"
            className={`sub-menu-mobile  ${context.showContact ? 'show-contact' : ''}`}
            contactUs={contactUs}
            contactInfo={contactInfo}
            contactBackButtonLabel={contactBackButtonLabel}
          />
        </FocusOn>
      )}
    </div>
  ) : (
    <div className={`side-nav-desktop ${staticSubMenuNavId ? 'static-sub-menu' : ''}`} id="navbar">
      {organizationCategory === ORG_CATEGORY_AGENCY || organizationCategory === ORG_CATEGORY_BROKER ? (
        <BrandingLogo {...logo} />
      ) : null}

      <MenuList
        userName={userName}
        isAuthenticated={isAuthenticated}
        staticSubMenuNavId={staticSubMenuNavId}
        items={finalMenuList}
        languageToggleLabel={languageToggleLabel}
        languageToggleAriaLabel={languageToggleAriaLabel}
        languageToggleLink={languageToggleLink}
        profile={profile}
        id={'nav-desktop-control'}
        onLanguageToggle={onLanguageToggle}
      />
    </div>
  );
};

export interface SideNavProps extends NavMenu {
  isAuthenticated: boolean;
  organizationCode?: string;
  organizationCategory?: string;
  claims?: string[];
  isAdmin?: boolean;
  organizationDropDownList?: {
    label: string;
    value: string;
  }[];
  searchOrganizations?: (str: string) => [];
  organizationSelectionChange?: (val: string) => void;
  organizationText?: string;
}
export const SideNav: FC<SideNavProps> = props => (
  <NavContextProvider defaultActive={props.defaultSelected} items={props.menuList} onSelect={props.onSelect}>
    <nav className="side-nav">
      <Navigation {...props} />
    </nav>
  </NavContextProvider>
);

SideNav.displayName = 'SideNav';
export default SideNav;
